import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar, { SidebarProvider } from "../../components/navigation/Sidebar";
import { supabase } from "../../lib/supabase";
import { generatePinFromArticle, insertPinToLibrary } from "../../services/pinImageAutomationService";
import { articleQueueWorker } from "../../services/articleQueueWorker";
import { useModal } from "../../components/ui/ModalProvider";
import { recoveryRequest } from "../../services/pinterestRecoveryService";
import { generatePinterestContent } from "../../services/pinterestPinService";
import { useLanguageProfile } from "../../contexts/LanguageProfileContext";

const templateOptions = [
  {
    id: "template1",
    src: "https://anhhqifbclbhvrbcssoe.supabase.co/storage/v1/object/public/templates/template%2001.png",
    alt: "Template 1"
  },
  {
    id: "template_2",
    src: "https://images.danarecipes.com/pins/1787014179325-61tk1qidf6-pin_354_1787014179324.webp",
    alt: "Template 2"
  },
  {
    id: "template_3",
    src: "https://images.danarecipes.com/pins/1786988026345-g9bvrgvu2x-pin_13120_1786988026345.webp",
    alt: "Template 3"
  },
  {
    id: "template_4",
    src: "https://images.danarecipes.com/pins/1785935360189-nw3lrx35hl-pin_86_1785935360188.webp",
    alt: "Template 4"
  },
  {
    id: "template_5",
    src: "https://images.danarecipes.com/pins/1785940130959-7a37ok9lxfu-pin_4652_1785940130959.webp",
    alt: "Template 5"
  },
  {
    id: "template_6",
    src: "https://images.danarecipes.com/pins/1786986673570-zuonov0c0fo-pin_13154_1786986673569.webp",
    alt: "Template 6"
  },
  {
    id: "template_7",
    src: "https://images.danarecipes.com/pins/1787008640243-95x5sgaxshw-pin_13130_1787008640243.webp",
    alt: "Template 7"
  },
  {
    id: "template_8",
    src: "https://images.danarecipes.com/pins/1786989634283-k92unu0mala-pin_4653_1786989634283.webp",
    alt: "Template 8"
  },
  {
    id: "template_9",
    src: "https://anhhqifbclbhvrbcssoe.supabase.co/storage/v1/object/public/templates/template_1_Exm.png",
    alt: "Template 9"
  },
  {
    id: "template_10",
    src: "https://pub-0bf936ed4af64b2083e8cca183d0a5b5.r2.dev/pins/1781533761716-r61g5ci31yo-pin_12135_1781533761715.webp",
    alt: "Template 10"
  },
  {
    id: "template_11",
    src: "https://pub-0bf936ed4af64b2083e8cca183d0a5b5.r2.dev/pins/1781532122549-gqg1x2ksscl-pin_12128_1781532122549.webp",
    alt: "Template 11"
  },
  {
    id: "template_12",
    src: "https://pub-0bf936ed4af64b2083e8cca183d0a5b5.r2.dev/pins/1781532968873-gck3vbap9zq-pin_12101_1781532968873.webp",
    alt: "Template 12"
  },
  {
    id: "template_13",
    src: "https://pub-0bf936ed4af64b2083e8cca183d0a5b5.r2.dev/pins/1781533147044-1ijlcjaocu2-pin_12100_1781533147044.webp",
    alt: "Template 13"
  },
  {
    id: "template_14",
    src: "https://pub-0bf936ed4af64b2083e8cca183d0a5b5.r2.dev/pins/1781533225974-f5x8r91iiue-pin_12103_1781533225974.webp",
    alt: "Template 14"
  },
  {
    id: "template_15",
    src: "https://pub-0bf936ed4af64b2083e8cca183d0a5b5.r2.dev/pins/1781533254258-4w60iv2c293-pin_12078_1781533254258.webp",
    alt: "Template 15"
  }
];

const etsyTemplateOptions = [
  {
    id: "etsy_template_1",
    src: "https://anhhqifbclbhvrbcssoe.supabase.co/storage/v1/object/public/templates/PIN%20IT,%20TRY%20IT%20LATER.png",
    alt: "Etsy Template 1"
  },
  {
    id: "etsy_template_2",
    src: "https://anhhqifbclbhvrbcssoe.supabase.co/storage/v1/object/public/templates/PIN%20IT,%20TRY%20IT%20LATER%20(1).png",
    alt: "Etsy Template 2"
  },
  {
    id: "etsy_template_3",
    src: "https://anhhqifbclbhvrbcssoe.supabase.co/storage/v1/object/public/templates/PIN%20IT,%20TRY%20IT%20LATER%20(2).png",
    alt: "Etsy Template 3"
  }
];

// Read the complete published-post index, not just the visible article page.
async function loadWordPressIndex(siteUrl, signal) {
  const posts = [];
  for (let page = 1; ; page++) {
    const params = new URLSearchParams({ per_page: '100', page: String(page),
      _fields: 'id,date,title', orderby: 'date', order: 'desc', status: 'publish' });
    const response = await fetch(`${siteUrl.replace(/\/$/, '')}/wp-json/wp/v2/posts?${params}`, { signal });
    const data = await response.json();
    if (response.status === 400 && data?.code === 'rest_post_invalid_page_number') break;
    if (!response.ok || !Array.isArray(data)) throw new Error('Unable to load article dates and search index. Switch websites and try again.');
    posts.push(...data);
    const totalPages = Number(response.headers.get('X-WP-TotalPages'));
    if (totalPages > 0 ? page >= totalPages : data.length < 100) break;
  }
  return posts;
}

function filterWordPressIndex(posts, year, month, search) {
  const query = search.trim().toLowerCase();
  return posts.filter(post => (!year || post.date?.slice(0, 4) === year) &&
    (!month || post.date?.slice(5, 7) === month) &&
    (!query || (post.title?.rendered || '').toLowerCase().includes(query)));
}

export default function CreatePin() {
  const { alert } = useModal();
  const navigate = useNavigate();
  const { activeProfile, profiles, setActiveProfile } = useLanguageProfile();
  const location = useLocation();
  const recoveryInput = location.state?.recovery || null;
  const [recoveryData, setRecoveryData] = useState(null);
  const [recoveryError, setRecoveryError] = useState('');
  const [recoveryConfirmed, setRecoveryConfirmed] = useState(false);
  const [pinDrafts, setPinDrafts] = useState({});
  const [reviewArticleId, setReviewArticleId] = useState(null);
  const [generatingText, setGeneratingText] = useState(false);
  const [textFailures, setTextFailures] = useState([]);
  const [textProgress, setTextProgress] = useState({ completed: 0, total: 0 });
  const textBusy = useRef(false);
  const textRequest = useRef(0);
  useEffect(() => () => { textRequest.current++; }, []);
  const draftScopeRef = useRef('');
  const recoveryProfileSelected = useRef(false);


  // Prevent the page from scrolling horizontally while Create Pin is mounted.
  // overflow-x: hidden on child divs does not stop the document scroll container;
  // it must be set on html/body directly, scoped here so no other page is affected.
  React.useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflowX;
    const prevBody = body.style.overflowX;
    html.style.overflowX = 'hidden';
    body.style.overflowX = 'hidden';
    return () => {
      html.style.overflowX = prevHtml;
      body.style.overflowX = prevBody;
    };
  }, []);
  const functionsUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const [selectedTemplate, setSelectedTemplate] = useState("template1");
  const [selectedSite, setSelectedSite] = useState("");
  const [articles, setArticles] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [wpPage, setWpPage] = useState(2);
  const [articleYear, setArticleYear] = useState('');
  const [articleMonth, setArticleMonth] = useState('');
  const [articleIndex, setArticleIndex] = useState(null);
  const [articleError, setArticleError] = useState('');
  const [useFeaturedImage, setUseFeaturedImage] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({ completed: 0, total: 0 });
  const articleView = useRef('');
  const indexSite = useRef('');
  const [hasMoreArticles, setHasMoreArticles] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedArticleIds, setSelectedArticleIds] = useState([]);
  const [sites, setSites] = useState([]);
  const [pinterestAccounts, setPinterestAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsError, setAccountsError] = useState(null);
  const [selectedPinterestAccount, setSelectedPinterestAccount] = useState("");
  const [selectedAccountHasBoardsWrite, setSelectedAccountHasBoardsWrite] =
    useState(true);
  const [boards, setBoards] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [boardsError, setBoardsError] = useState(null);
  const [selectedBoardId, setSelectedBoardId] = useState("");
  const [generatedPins, setGeneratedPins] = useState([]);
  const [selectedGeneratedPinId, setSelectedGeneratedPinId] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleDateTime, setScheduleDateTime] = useState("");
  const [scheduleError, setScheduleError] = useState("");
  const formatErrorDetails = (error) => error?.stack || error?.message || String(error);

  // ── Content source ('wordpress' | 'etsy') ──────────────────────────────────
  const [contentSource, setContentSource] = useState("wordpress");
  const [etsyAccounts, setEtsyAccounts] = useState([]);
  const [selectedEtsyAccount, setSelectedEtsyAccount] = useState("");
  const [loadingEtsyAccounts, setLoadingEtsyAccounts] = useState(true);
  const [etsyAccountsError, setEtsyAccountsError] = useState(null);
  const [isSyncingListings, setIsSyncingListings] = useState(false);
  const [etsyListingsError, setEtsyListingsError] = useState(null);
  const [etsyTotal, setEtsyTotal] = useState(0);
  const [etsyOffset, setEtsyOffset] = useState(0);
  const [etsyHasMore, setEtsyHasMore] = useState(false);
  const [isLoadingMoreListings, setIsLoadingMoreListings] = useState(false);
  const [useListingMainImage, setUseListingMainImage] = useState(false);

  const buildPinDescription = (article) => {
    const base =
      article?.meta_description ||
      article?.description ||
      article?.shortDescription ||
      article?.summary ||
      article?.excerpt ||
      article?.short_description ||
      "";

    if (base && base.trim()) return base.trim();

    return article?.title || '';
  };
  const buildPinTags = (article) => {
    if (!article) return [];
    const sources = [
      article?.focusKeyword,
      article?.title,
      article?.shortDescription,
      article?.meta_description,
      article?.description,
      article?.seoTitle,
      article?.permalink,
      (article?.secondaryKeywords || []).join(" "),
      (article?.tags || []).join(" ")
    ];

    const clean = (text) =>
      (text || "")
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    const tagsSet = [];
    const pushTag = (value) => {
      const normalized = clean(value);
      if (!normalized) return;
      if (!tagsSet.includes(normalized)) {
        tagsSet.push(normalized);
      }
    };

    sources.forEach((source) => {
      if (!source) return;
      const parts = source.toString().split(/[,|/]/);
      parts.forEach((part) => pushTag(part));
    });

    if (tagsSet.length < 8 && article?.focusKeyword) {
      article.focusKeyword.split(/\s+/).forEach((word) => pushTag(word));
    }

    if (tagsSet.length < 8 && article?.title) {
      article.title.split(/\s+/).forEach((word) => pushTag(word));
    }

    const fallbackTerms = ["recipe", "pinterest", "cooking", "food", "delicious"];
    let fallbackIndex = 0;
    while (tagsSet.length < 8) {
      pushTag(fallbackTerms[fallbackIndex % fallbackTerms.length]);
      fallbackIndex += 1;
    }

    return tagsSet.slice(0, 8);
  };
  
  const WP_PAGE_SIZE = 20;

  function decodeHtmlEntities(str) {
    return str.replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
              .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"');
  }

  function extractSecondImageFromContent(contentHtml, mainImageUrl = null) {
    if (!contentHtml) return null;
    const matches = [...contentHtml.matchAll(/<img[^>]+src=["']([^"']+)["']/gi)];
    // Decode HTML entities from each extracted URL before comparing/returning
    const decoded = matches.map(m => decodeHtmlEntities(m[1]));
    // Prefer storage.gymplix.com images first, skipping the main image
    const storageImg = decoded.find(url => url.includes('storage.gymplix.com') && url !== mainImageUrl);
    if (storageImg) return storageImg;
    // Fallback to first img found that isn't the main image
    return decoded.find(url => url !== mainImageUrl) || null;
  }

  async function fetchWPArticlesPage(siteUrl, page, ids = null, signal) {
    if (!siteUrl) return [];
    try {
      if (ids && !ids.length) return [];
      const params = new URLSearchParams({ _embed: '', per_page: String(WP_PAGE_SIZE),
        page: String(ids ? 1 : page), orderby: 'date', order: 'desc' });
      if (ids) params.set('include', ids.join(','));
      const res = await fetch(`${siteUrl.replace(/\/$/, '')}/wp-json/wp/v2/posts?${params}`, { signal });
      if (!res.ok) return [];
      const data = await res.json();
      if (!Array.isArray(data)) return [];
      const urls = data.map(post => post.link).filter(Boolean);
      let storedArticles = [];
      if (activeProfile?.id && selectedSite && urls.length) {
        const quotedUrls = urls.map(url => JSON.stringify(url)).join(',');
        const { data: stored } = await supabase.from('wp_articles').select('wp_post_url,wordpress_url,permalink,focus_keyword,secondary_keywords,meta_description,short_description')
          .eq('profile_id', activeProfile.id).eq('site_id', selectedSite)
          .or(['wp_post_url','wordpress_url','permalink'].map(column => `${column}.in.(${quotedUrls})`).join(','));
        storedArticles = stored || [];
      }
      const plainText = html => new DOMParser().parseFromString(html || '', 'text/html').body.textContent?.trim() || '';
      return data.map((post) => {
        const stored = storedArticles.find(article => [article.wp_post_url,article.wordpress_url,article.permalink].includes(post.link));
        const mainImageUrl = post.external_featured_image || post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || null;
        return {
          id: post.id,
          title: plainText(post.title?.rendered),
          focusKeyword: stored?.focus_keyword || post.focus_keyword || post.meta?._yoast_wpseo_focuskw || '',
          focus_keywords: Array.isArray(stored?.secondary_keywords) ? stored.secondary_keywords.join(', ') : stored?.secondary_keywords || '',
          meta_description: stored?.meta_description || stored?.short_description || plainText(post.excerpt?.rendered).slice(0, 500),
          articleContext: plainText(post.content?.rendered).slice(0, 16000),
          date: post.date,
          image: mainImageUrl,
          mainImage: mainImageUrl,
          secondImage: extractSecondImageFromContent(post.content?.rendered, mainImageUrl),
          url: post.link || null,
          source: "wordpress",
        };
      });
    } catch {
      return [];
    }
  }

  // Only show websites owned by the active profile; ignore stale profile requests.
  React.useEffect(() => {
    let cancelled = false;
    setSites([]); setArticles([]); setSelectedArticleIds([]);
    async function loadSites() {
      if (!activeProfile?.id) return;
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data, error } = await supabase.from('wp_sites').select('id, site_url, site_name').eq('user_id', user.id).eq('profile_id', activeProfile.id);
        if (error) throw error;
        if (!cancelled) setSites((data || []).map(site => ({ id: site.id, name: site.site_name || new URL(site.site_url).hostname, url: site.site_url })));
      } catch (error) { if (!cancelled) setArticleError('Unable to load websites for this profile.'); }
    }
    loadSites();
    return () => { cancelled = true; };
  }, [activeProfile?.id]);

  const getSupabaseAccessToken = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token || null;
  };

  const loadPinterestAccounts = async () => {
    if (!functionsUrl) {
      setAccountsError({
        title: "Pinterest services are not available right now.",
        details: "",
      });
      setAccountsLoading(false);
      return;
    }

    if (!supabaseAnonKey) {
      setAccountsError({
        title: "Missing Supabase anonymous key.",
        details: "",
      });
      setAccountsLoading(false);
      return;
    }

    setAccountsLoading(true);
    setAccountsError("");

    try {
      const token = await getSupabaseAccessToken();
      if (!token) {
        throw new Error("User authentication expired. Please re-login.");
      }

    const accountsUrl = activeProfile?.id
      ? `${functionsUrl}/pinterest-accounts?profileId=${encodeURIComponent(activeProfile.id)}`
      : `${functionsUrl}/pinterest-accounts`;
    const res = await fetch(accountsUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        apikey: supabaseAnonKey,
      },
    });

    if (!res.ok) {
      throw new Error("Unable to load Pinterest accounts.");
    }

    const payload = await res.json();
    setPinterestAccounts(payload?.accounts || []);
    // Update the boards:write flag for the current selection
    const selectedAccount = payload?.accounts?.find(
      (acct) => acct?.id === selectedPinterestAccount
    );
    setSelectedAccountHasBoardsWrite(
      selectedAccount?.has_boards_write !== false
    );
    setAccountsError(null);
    } catch (error) {
      console.error("Failed to load Pinterest accounts", error);
      setAccountsError({
        title: "Failed to load Pinterest accounts",
        details: formatErrorDetails(error),
      });
      setPinterestAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  };

  const loadEtsyAccounts = async () => {
    if (!functionsUrl) {
      setEtsyAccountsError({ title: "Etsy services are not available right now.", details: "" });
      setLoadingEtsyAccounts(false);
      return;
    }
    if (!supabaseAnonKey) {
      setEtsyAccountsError({ title: "Missing Supabase anonymous key.", details: "" });
      setLoadingEtsyAccounts(false);
      return;
    }
    setLoadingEtsyAccounts(true);
    setEtsyAccountsError(null);
    try {
      const token = await getSupabaseAccessToken();
      if (!token) throw new Error("User authentication expired. Please re-login.");
      const res = await fetch(`${functionsUrl}/etsy-accounts?profileId=${encodeURIComponent(activeProfile.id)}`, {
        headers: { Authorization: `Bearer ${token}`, apikey: supabaseAnonKey },
      });
      if (!res.ok) throw new Error("Unable to load Etsy accounts.");
      const payload = await res.json();
      setEtsyAccounts(payload?.accounts || []);
      setEtsyAccountsError(null);
    } catch (error) {
      setEtsyAccountsError({ title: "Failed to load Etsy accounts", details: formatErrorDetails(error) });
      setEtsyAccounts([]);
    } finally {
      setLoadingEtsyAccounts(false);
    }
  };

  const normalizeEtsyListings = (raw) =>
    raw.map((listing) => ({
      id:          listing.listing_id ?? listing.id,
      title:       listing.title || "Untitled Listing",
      date:        listing.date ?? new Date().toISOString(),
      image:       listing.mainImage ?? null,
      mainImage:   listing.mainImage ?? null,
      secondImage: null,
      url:         listing.url,
      source:      "etsy",
    }));

  const handleSyncEtsyListings = async () => {
    if (!selectedEtsyAccount || !functionsUrl) return;
    setIsSyncingListings(true);
    setLoadingArticles(true);
    setEtsyListingsError(null);
    setArticles([]);
    setSelectedArticleIds([]);
    setSearchTerm("");
    setEtsyOffset(0);
    setEtsyHasMore(false);
    setEtsyTotal(0);
    try {
      const token = await getSupabaseAccessToken();
      if (!token) throw new Error("Authentication expired. Please refresh.");
      const res = await fetch(
        `${functionsUrl}/etsy-listings?accountId=${encodeURIComponent(selectedEtsyAccount)}&profileId=${encodeURIComponent(activeProfile.id)}&offset=0&limit=10`,
        { headers: { Authorization: `Bearer ${token}`, apikey: supabaseAnonKey } }
      );
      if (!res.ok) {
        let errPayload = null;
        try { errPayload = await res.json(); } catch { /* not json */ }
        throw new Error(errPayload?.error || `Failed to sync Etsy listings (status ${res.status})`);
      }
      const payload = await res.json();
      const listings = normalizeEtsyListings(payload?.listings || []);
      setArticles(listings);
      setEtsyTotal(payload.total ?? listings.length);
      setEtsyOffset(listings.length);
      setEtsyHasMore(payload.hasMore ?? false);
      if (listings.length === 0) setEtsyListingsError("No active listings found for this account.");
    } catch (error) {
      setEtsyListingsError(error.message || "Failed to sync listings");
      setArticles([]);
    } finally {
      setIsSyncingListings(false);
      setLoadingArticles(false);
    }
  };

  const handleLoadMoreListings = async () => {
    if (!selectedEtsyAccount || !functionsUrl || isLoadingMoreListings || !etsyHasMore) return;
    setIsLoadingMoreListings(true);
    try {
      const token = await getSupabaseAccessToken();
      if (!token) throw new Error("Authentication expired. Please refresh.");
      const res = await fetch(
        `${functionsUrl}/etsy-listings?accountId=${encodeURIComponent(selectedEtsyAccount)}&profileId=${encodeURIComponent(activeProfile.id)}&offset=${etsyOffset}&limit=10`,
        { headers: { Authorization: `Bearer ${token}`, apikey: supabaseAnonKey } }
      );
      if (!res.ok) throw new Error(`Failed to load more listings (status ${res.status})`);
      const payload = await res.json();
      const newListings = normalizeEtsyListings(payload?.listings || []);
      setArticles((prev) => [...prev, ...newListings]);
      setEtsyOffset((prev) => prev + newListings.length);
      setEtsyHasMore(payload.hasMore ?? false);
    } catch (error) {
      setEtsyListingsError(error.message || "Failed to load more listings");
    } finally {
      setIsLoadingMoreListings(false);
    }
  };

  React.useEffect(() => {
    loadPinterestAccounts();
    loadEtsyAccounts();
  }, [activeProfile?.id]);

  React.useEffect(() => {
    if (!selectedPinterestAccount) {
      setSelectedAccountHasBoardsWrite(true);
      return;
    }
    const account = pinterestAccounts.find((acct) => acct?.id === selectedPinterestAccount);
    setSelectedAccountHasBoardsWrite(account?.has_boards_write !== false);
  }, [selectedPinterestAccount, pinterestAccounts]);

  const loadBoardsForAccount = async (accountId) => {
  if (!accountId) return;
  if (!functionsUrl) {
    setBoardsError({
      title: "Pinterest services are not configured.",
      details: "",
    });
    return;
  }

  if (!supabaseAnonKey) {
    setBoardsError({
      title: "Missing Supabase anonymous key.",
      details: "",
    });
    return;
  }

  setBoardsLoading(true);
  setBoardsError(null);
    setBoards([]);
    setSelectedBoardId("");

    try {
      const token = await getSupabaseAccessToken();
      if (!token) {
        throw new Error("Session expired. Please refresh the page.");
      }

      const url = `${functionsUrl}/pinterest-boards?accountId=${encodeURIComponent(accountId)}`;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: supabaseAnonKey,
        },
      });

      if (!res.ok) {
        let payload = null;
        try { payload = await res.json(); } catch { /* not json */ }
        if (payload?.needsReconnect) {
          setBoardsError({ title: payload.error, details: "", needsReconnect: true });
          setBoards([]);
          return;
        }
        const errorText = payload ? JSON.stringify(payload) : await res.text().catch(() => "");
        throw new Error(
          `Unable to load Pinterest boards (status ${res.status}): ${errorText}`
        );
      }

      const payload = await res.json();
      setBoards(payload?.boards || []);
      setBoardsError(null);
    } catch (error) {
      console.error("Failed to load Pinterest boards", error);
      setBoardsError({
        title: "Failed to load Pinterest boards",
        details: formatErrorDetails(error),
      });
      setBoards([]);
    } finally {
      setBoardsLoading(false);
    }
  };

  React.useEffect(() => {
  if (!selectedPinterestAccount) {
    setBoards([]);
    setBoardsError(null);
    setSelectedBoardId("");
    return;
  }

    loadBoardsForAccount(selectedPinterestAccount);
  }, [selectedPinterestAccount]);

  // ✅ START/STOP article queue worker based on "myblog" selection (WordPress only)
  React.useEffect(() => {
    if (contentSource !== "wordpress") {
      articleQueueWorker.stop();
      return () => { articleQueueWorker.stop(); };
    }
    const selectedSiteData = sites?.find((s) => s?.id === selectedSite);
    const isMyblogSelected = selectedSiteData && !selectedSiteData?.url;

    if (isMyblogSelected) {
      articleQueueWorker.start();
    } else {
      articleQueueWorker.stop();
    }

    return () => {
      articleQueueWorker.stop();
    };
  }, [selectedSite, sites, contentSource]);

  // Recovery handoff only prepares existing Create Pin state; it never generates or publishes.
  useEffect(() => {
    if (!recoveryInput || recoveryProfileSelected.current || !profiles.length) return;
    recoveryProfileSelected.current = true;
    if (!profiles.some(p => p.id === recoveryInput.profileId)) { setRecoveryError('Recovery profile is unavailable.'); return; }
    if (activeProfile?.id !== recoveryInput.profileId) setActiveProfile(recoveryInput.profileId);
  }, [recoveryInput, profiles, activeProfile?.id, setActiveProfile]);
  useEffect(() => {
    if (!recoveryInput) { setRecoveryData(null); setRecoveryError(''); return; }
    setRecoveryData(null); setRecoveryConfirmed(false); setArticles([]); setSelectedArticleIds([]);
    if (activeProfile?.id !== recoveryInput.profileId) { setRecoveryError('Select the recovery recommendation’s profile to continue.'); return; }
    const controller = new AbortController();
    setLoadingArticles(true); setRecoveryError('');
    recoveryRequest('handoff', recoveryInput, controller.signal).then(data => {
      if (controller.signal.aborted) return;
      setRecoveryData(data); setContentSource('wordpress'); setSelectedSite(data.siteId);
      setSelectedPinterestAccount(data.accountId); setUseFeaturedImage(false);
      setSearchTerm(''); setArticleYear(''); setArticleMonth(''); setHasMoreArticles(false);
      const prepared = data.articles.map(a => ({
        id: a.id, title: a.title, date: a.published_at, url: a.destination_url || a.wp_post_url || a.wordpress_url,
        mainImage: a.image_main_url, image: a.image_main_url, secondImage: a.image_middle_url,
        focusKeyword: a.focus_keyword, focus_keyword: a.focus_keyword,
        focus_keywords: Array.isArray(a.focus_keywords) ? a.focus_keywords.join(', ') : a.focus_keywords,
        meta_description: a.meta_description, shortDescription: a.short_description, source: 'wordpress',
      }));
      setArticles(prepared); setSelectedArticleIds(prepared.map(a => a.id));
    }).catch(e => { if (!controller.signal.aborted) setRecoveryError(e.message); })
      .finally(() => { if (!controller.signal.aborted) setLoadingArticles(false); });
    return () => controller.abort();
  }, [recoveryInput, activeProfile?.id]);
  const draftScope = JSON.stringify([activeProfile?.id, contentSource, selectedSite, selectedEtsyAccount]);
  draftScopeRef.current = draftScope;
  const draftKey = article => `${draftScope}:${article.id}`;
  const draftFor = article => pinDrafts[draftKey(article)] || { pinTitle: article.title || '', pinDescription: buildPinDescription(article), history: [] };
  const selectedArticles = articles.filter(a => selectedArticleIds.includes(a.id));
  const reviewArticle = selectedArticles.find(a => a.id === reviewArticleId) || selectedArticles[0];
  const reviewDraft = reviewArticle ? draftFor(reviewArticle) : { pinTitle: '', pinDescription: '' };
  const reviewImage = reviewArticle?.mainImage || reviewArticle?.image || reviewArticle?.featured_image_url || reviewArticle?.featuredImage;
  useEffect(() => {
    textRequest.current++; textBusy.current = false; setGeneratingText(false); setTextFailures([]); setTextProgress({ completed: 0, total: 0 }); setReviewArticleId(null);
  }, [draftScope]);
  const toggleArticle = id => {
    setSelectedArticleIds(old => old.includes(id) ? old.filter(value => value !== id) : [...old, id]);
    setReviewArticleId(id);
  };
  const updateReviewDraft = (field, value) => {
    if (!reviewArticle) return;
    const key = draftKey(reviewArticle);
    setPinDrafts(old => ({ ...old, [key]: { ...draftFor(reviewArticle), [field]: value } }));
    setRecoveryConfirmed(false);
  };
  const handleGenerateText = async () => {
    if (!selectedArticles.length || textBusy.current || isGenerating || !activeProfile?.id) return;
    const batch = selectedArticles.map(article => ({ article, key: draftKey(article), current: draftFor(article) }));
    const scope = draftScope, request = ++textRequest.current;
    const isCurrent = () => draftScopeRef.current === scope && request === textRequest.current;
    textBusy.current = true;
    setGeneratingText(true); setTextFailures([]); setTextProgress({ completed: 0, total: batch.length });
    try {
      // Sequential requests keep provider load bounded; failures do not stop other articles.
      for (const { article, key, current } of batch) {
        if (!isCurrent()) return;
        try {
          const result = await generatePinterestContent(article.title, buildPinDescription(article), article.focusKeyword?.trim() || article.focus_keyword?.trim() || '', activeProfile.language_code, {
            articleContext: article.articleContext || article.shortDescription || article.description || '',
            freshVariation: true, profileId: activeProfile.id, articleUrl: article.url || article.link || article.permalink,
            relatedKeywords: article.focus_keywords || article.secondaryKeywords || [],
            previousVersions: [...(current.history || []), { pinTitle: current.pinTitle, pinDescription: current.pinDescription }],
          });
          if (!isCurrent()) return;
          setPinDrafts(old => ({ ...old, [key]: { ...result, history: [...(current.history || []), { pinTitle: current.pinTitle, pinDescription: current.pinDescription }] } }));
          setTextProgress(old => ({ ...old, completed: old.completed + 1 }));
          setRecoveryConfirmed(false);
        } catch (error) {
          if (!isCurrent()) return;
          setTextFailures(old => [...old, { articleId: article.id, title: article.title, message: error.message || 'Text generation failed.' }]);
        }
      }
    } finally {
      if (isCurrent()) { textBusy.current = false; setGeneratingText(false); }
    }
  };

  // Etsy retains its existing loaded-list search; WordPress uses the full index.
  const filteredArticles = useMemo(() => {
    const q = searchTerm?.trim()?.toLowerCase();
    if (contentSource === 'wordpress' || !q) return articles;
    return articles.filter(a => (a?.title || '').toLowerCase().includes(q));
  }, [articles, searchTerm, contentSource]);
  const visibleArticles = filteredArticles;
  const canShowMore = hasMoreArticles;
  const wpFiltered = Boolean(articleYear || articleMonth || searchTerm.trim());
  const matchingPosts = useMemo(() => filterWordPressIndex(articleIndex || [], articleYear, articleMonth, searchTerm),
    [articleIndex, articleYear, articleMonth, searchTerm]);
  const availableYears = useMemo(() => [...new Set((articleIndex || []).map(post => post.date?.slice(0, 4)).filter(Boolean))].sort().reverse(), [articleIndex]);
  const wpSite = sites.find(site => site.id === selectedSite);
  const wpSiteKey = JSON.stringify([activeProfile?.id, wpSite?.id, wpSite?.url]);
  const wpView = JSON.stringify([wpSiteKey, contentSource, articleYear, articleMonth, searchTerm]);
  articleView.current = wpView;

  const handleSiteSelection = useCallback(siteId => {
    setSelectedSite(siteId); setArticles([]); setSearchTerm('');
    setArticleYear(''); setArticleMonth(''); setSelectedArticleIds([]);
    setHasMoreArticles(false); setWpPage(2);
  }, []);

  React.useEffect(() => {
    const controller = new AbortController();
    if (recoveryInput) return () => controller.abort();
    indexSite.current = wpSiteKey;
    setArticleIndex(null); setArticleError('');
    if (wpSite?.url && contentSource === 'wordpress') {
      loadWordPressIndex(wpSite.url, controller.signal).then(posts => {
        if (!controller.signal.aborted) setArticleIndex(posts);
      }).catch(error => {
        if (!controller.signal.aborted) setArticleError(error.message);
      });
    }
    return () => controller.abort();
  }, [wpSiteKey, contentSource, recoveryInput]);

  React.useEffect(() => {
    if (contentSource !== 'wordpress' || recoveryInput) return;
    const controller = new AbortController();
    const view = wpView;
    setArticles([]); setSelectedArticleIds([]); setHasMoreArticles(false); setWpPage(2);
    setIsLoadingMore(false);
    if (!wpSite?.url) { setLoadingArticles(false); return; }
    if (wpFiltered && (!articleIndex || indexSite.current !== wpSiteKey)) {
      setLoadingArticles(!articleError); return;
    }
    setLoadingArticles(true);
    const timer = setTimeout(async () => {
      const ids = wpFiltered ? matchingPosts.slice(0, WP_PAGE_SIZE).map(post => post.id) : null;
      const rows = await fetchWPArticlesPage(wpSite.url, 1, ids, controller.signal);
      if (controller.signal.aborted || articleView.current !== view) return;
      setArticles(rows);
      setHasMoreArticles(wpFiltered ? matchingPosts.length > WP_PAGE_SIZE : rows.length === WP_PAGE_SIZE);
      setLoadingArticles(false);
    }, searchTerm ? 250 : 0);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [wpView, wpFiltered ? articleIndex : null, wpFiltered ? articleError : null, recoveryInput]);

  const handleLoadMore = async () => {
    if (!hasMoreArticles || isLoadingMore || !wpSite?.url) return;
    const view = wpView;
    setIsLoadingMore(true);
    const ids = wpFiltered ? matchingPosts.slice((wpPage - 1) * WP_PAGE_SIZE, wpPage * WP_PAGE_SIZE).map(post => post.id) : null;
    const rows = await fetchWPArticlesPage(wpSite.url, wpPage, ids);
    if (articleView.current !== view) return;
    setArticles(previous => {
      const seen = new Set(previous.map(row => row.id));
      return [...previous, ...rows.filter(row => !seen.has(row.id))];
    });
    setWpPage(page => page + 1);
    setHasMoreArticles(wpFiltered ? matchingPosts.length > wpPage * WP_PAGE_SIZE : rows.length === WP_PAGE_SIZE);
    setIsLoadingMore(false);
  };

  React.useEffect(() => {
    if (contentSource !== 'wordpress' || recoveryInput) return;
    if (sites.length && !sites.some(site => site.id === selectedSite)) handleSiteSelection(sites[0].id);
  }, [sites, selectedSite, handleSiteSelection, contentSource, recoveryInput]);

  // Auto-select the Etsy account only when exactly one is connected; with
  // multiple accounts the user still picks manually via the dropdown, and the
  // `!selectedEtsyAccount` guard avoids overriding a manual selection.
  React.useEffect(() => {
    if (contentSource !== "etsy") return;
    if (etsyAccounts.length === 1 && !selectedEtsyAccount) {
      setSelectedEtsyAccount(etsyAccounts[0].id);
    }
  }, [contentSource, etsyAccounts, selectedEtsyAccount]);

  // Defined here so handleSiteSelection (above) is already in scope
  function handleContentSourceChange(newSource) {
    if (newSource === contentSource) return;
    setContentSource(newSource);
    setArticles([]);
    setSelectedArticleIds([]);
    setSearchTerm("");
    setEtsyListingsError(null);
    if (newSource === "wordpress") setUseListingMainImage(false);
    if (newSource === "wordpress" && selectedSite) {
      handleSiteSelection(selectedSite);
    }
    if (newSource === "etsy") setSelectedTemplate("etsy_template_1");
  }



async function handleGeneratePins() {
  if (generatingText || isGenerating || !activeProfile?.id) return;
  if (selectedArticles.some(article => !draftFor(article).pinTitle.trim() || !draftFor(article).pinDescription.trim())) { alert("Add a title and description for each selected article."); return; }
  if (recoveryInput && (!recoveryData || !recoveryConfirmed || !sites.some(site => site.id === recoveryData.siteId) || activeProfile?.id !== recoveryInput.profileId)) return;
  console.log("🔍 DEBUG: handleGeneratePins started");
  console.log("🔍 DEBUG: selectedArticleIds:", selectedArticleIds);
  
  if (!selectedArticleIds.length) {
    alert("Please select at least one article");
    return;
  }

  const selectedSiteData = sites.find((s) => s.id === selectedSite);
  const siteDomain = selectedSiteData?.name || "yoursite.com";
  console.log("🔍 DEBUG: siteDomain:", siteDomain);

  const selectedEtsyAccountData = etsyAccounts.find((a) => a.id === selectedEtsyAccount);
  const etsyShopName = selectedEtsyAccountData?.shop_name || "";

  setIsGenerating(true);
  setGenerationProgress({ completed: 0, total: selectedArticleIds.length });
  const generatedBatch = [];
  const failureReasons = [];

  const selectedBoard = boards.find((board) => board?.id === selectedBoardId) || null;
  const boardInfo = {
    pinterestAccountId: selectedPinterestAccount,
    boardId: selectedBoard?.id || null,
    boardName: selectedBoard?.name || selectedBoard?.title || selectedBoard?.id || null,
  };
  console.log("🔍 DEBUG: boardInfo:", boardInfo);

  try {
    console.log("🔍 DEBUG: Starting loop for", selectedArticleIds.length, "articles");
    
    for (const articleId of selectedArticleIds) {
      console.log("🔍 DEBUG: Processing articleId:", articleId);
      
      const article = articles.find((a) => a.id === articleId);
      console.log("🔍 DEBUG: Found article:", article ? {
        id: article.id,
        title: article.title,
        hasFocusKeyword: !!(article?.focusKeyword || article?.focus_keyword),
        focusKeyword: article?.focusKeyword || article?.focus_keyword,
        hasImage: !!(article?.mainImage || article?.featured_image_url || article?.image),
        imageSource: article?.mainImage || article?.featured_image_url || article?.image
      } : "NOT FOUND");

      if (!article) {
        console.warn("❌ DEBUG: Article not found in articles array");
        failureReasons.push(`Article ID ${articleId}: not found in loaded list`);
        continue;
      }

      const foodImageUrl =
        article?.mainImage ||
        article?.featured_image_url ||
        article?.image ||
        article?.imageUrl ||
        null;
      console.log("🔍 DEBUG: foodImageUrl resolved to:", foodImageUrl ? "VALID URL" : "NULL/EMPTY");

      const focusKeywordFromArticle = (article?.focusKeyword || article?.focus_keyword || "");
      const focusKeywordsFromArticle = (article?.focus_keywords || article?.focusKeywords || "");
      
      // Use focus_keyword ONLY - do NOT fall back to title
      const pinDisplayText = focusKeywordFromArticle || "";
      console.log("🔍 DEBUG: pinDisplayText resolved to:", pinDisplayText || "(EMPTY)");

      const articleTags = [];
      console.log("🔍 DEBUG: Built tags:", articleTags.length, "tags");

      if (!foodImageUrl) {
        console.warn("❌ DEBUG: Skipping article - no foodImageUrl");
        failureReasons.push(`"${article.title || articleId}": no image found`);
        continue;
      }

      const templateUrl =
        article.template_url ||
        article.template_image_url ||
        article.templateImageUrl ||
        article.templateImage ||
        article.template?.url ||
        article.template?.image_url ||
        null;

      const draft = draftFor(article);
      const articleTitleExact = draft.pinTitle;
      const articleDescriptionExact = draft.pinDescription;

      // Shared main-image path: Etsy listing image or WordPress featured image, without a template.
      if ((contentSource === "etsy" && useListingMainImage) || (contentSource === "wordpress" && useFeaturedImage)) {
        const articleUrlExact =
          article?.url ||
          article?.articleUrl ||
          article?.link ||
          article?.permalink ||
          (contentSource === "etsy" ? `https://www.etsy.com/listing/${article.id}` : "");

        // Use the same insertPinToLibrary used by the template path — no silent failures
        let pinRecord = null;
        try {
          pinRecord = await insertPinToLibrary({
            pinTitle:    articleTitleExact,
            description: articleDescriptionExact,
            imageUrl:    foodImageUrl,
            articleUrl:  articleUrlExact,
            templateId:  contentSource === "etsy" ? "listing_main_image" : "featured_image",
            websiteName: contentSource === "etsy" ? "etsy" : siteDomain,
            ...(contentSource === "wordpress" ? { profileId: activeProfile?.id || null, focusKeywords: focusKeywordsFromArticle || focusKeywordFromArticle } : {}),
            pinterestAccountId: boardInfo?.pinterestAccountId || null,
            boardId:     boardInfo?.boardId || null,
            boardName:   boardInfo?.boardName || null,
          });
          if (contentSource === "wordpress" && !pinRecord?.id) throw new Error('Pin was not saved to the library');
        } catch (dbErr) {
          failureReasons.push(`"${articleTitleExact}": failed to save — ${dbErr.message}`);
          continue;
        }

        generatedBatch.push({
          id:                pinRecord?.id || `${article.id}-${Date.now()}`,
          imageUrl:          foodImageUrl,
          displayText:       pinDisplayText,
          articleUrl:        articleUrlExact,
          sourceTitle:       articleTitleExact,
          sourceDescription: articleDescriptionExact,
          tags:              articleTags,
          templateId:        null,
        });
        setGenerationProgress({ completed: generatedBatch.length, total: selectedArticleIds.length });
        continue;
      }

      console.log("🔍 DEBUG: Calling generatePinFromArticle with:", {
        articleId: article.id,
        pinDisplayText,
        hasImageUrl: !!foodImageUrl,
        hasTemplateUrl: !!templateUrl
      });
      
      try {
        // For Etsy listings there is no focus_keyword — use the listing title so the
        // service's overlayText derivation always has something to work with.
        const effectiveFocusKeyword =
          focusKeywordFromArticle ||
          (contentSource === "etsy" ? articleTitleExact : "");

        const result = await generatePinFromArticle(
          {
            id: article.id,
            title: pinDisplayText || articleTitleExact,
            originalTitle: articleTitleExact,
            featured_image_url: foodImageUrl,
            second_image_url: article.secondImage || null,
            url: article.url || `https://www.etsy.com/listing/${article.id}`,
            template_url: templateUrl,
            focus_keywords: focusKeywordsFromArticle || effectiveFocusKeyword,
            focus_keyword: effectiveFocusKeyword,
            focusKeyword:  effectiveFocusKeyword,
            shopName: contentSource === "etsy" ? etsyShopName : undefined,
          },
          siteDomain,
          selectedTemplate,
          articleTags,
          articleDescriptionExact,
          boardInfo,
          activeProfile?.id || null
        );

        console.log("🔍 DEBUG: generatePinFromArticle result:", result ? {
          success: result.success,
          hasImageUrl: !!result.imageUrl,
          pinId: result.pinId
        } : "NULL/UNDEFINED");

        if (!result?.imageUrl) {
          console.warn("❌ DEBUG: No imageUrl in result, skipping");
          failureReasons.push(`"${article.title || articleId}": image generation returned no URL`);
          continue;
        }

        const previewId =
          result?.pinId ||
          result?.pinRecord?.id ||
          `${article.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

        const articleUrlExact =
          article?.url ||
          article?.articleUrl ||
          article?.link ||
          article?.permalink ||
          "";
        const previewPin = {
          id: previewId,
          imageUrl: result.imageUrl,
          displayText: pinDisplayText,
          articleUrl: articleUrlExact || "",
          sourceTitle: articleTitleExact,
          sourceDescription: articleDescriptionExact,
          tags: articleTags,
          templateId: selectedTemplate,
        };

        console.log("✅ DEBUG: Pin added to batch:", previewPin.id);
        generatedBatch.push(previewPin);
        setGenerationProgress({ completed: generatedBatch.length, total: selectedArticleIds.length });
      } catch (pinError) {
        console.error("❌ DEBUG: Failed to generate pin for article", articleId, pinError);
        console.error("❌ DEBUG: Error details:", pinError.message, pinError.stack);
        failureReasons.push(`"${article?.title || articleId}": ${pinError.message}`);
      }
    }

    console.log("🔍 DEBUG: Loop complete. generatedBatch.length:", generatedBatch.length);

    if (generatedBatch.length) {
      console.log("✅ DEBUG: Success - setting generated pins");
      setGeneratedPins((prev) => [...generatedBatch, ...prev]);
      setSelectedGeneratedPinId(generatedBatch[0].id);
      alert(
        `✅ Generated ${generatedBatch.length} pin${
          generatedBatch.length > 1 ? "s" : ""
        } and saved to Pin Library`
      );
    } else {
      console.error("❌ DEBUG: generatedBatch is empty - showing 'No pins were generated' alert");
      const reasonText = failureReasons.length
        ? "\n\nReasons:\n" + failureReasons.map((r) => `• ${r}`).join("\n")
        : "";
      alert(`⚠️ No pins were generated. Please verify the selected articles.${reasonText}`);
    }
  } catch (err) {
    console.error("❌ DEBUG: Outer error in handleGeneratePins", err);
    alert("❌ Error generating pins");
  } finally {
    setIsGenerating(false);
    console.log("🔍 DEBUG: handleGeneratePins complete");
  }
}

  React.useEffect(() => {
    if (!generatedPins.length) {
      setSelectedGeneratedPinId(null);
      return;
    }

    if (!selectedGeneratedPinId || !generatedPins.some((pin) => pin.id === selectedGeneratedPinId)) {
      setSelectedGeneratedPinId(generatedPins[0].id);
    }
  }, [generatedPins, selectedGeneratedPinId]);

  const activeGeneratedPin = useMemo(() => {
    if (!generatedPins.length) return null;
    const matched = generatedPins.find((pin) => pin.id === selectedGeneratedPinId);
    return matched || generatedPins[0];
  }, [generatedPins, selectedGeneratedPinId]);

  const canPerformPinActions = Boolean(
    activeGeneratedPin &&
      selectedPinterestAccount &&
      selectedBoardId &&
      selectedAccountHasBoardsWrite
  );
  const publishDisabled = !canPerformPinActions || isPublishing;
  const scheduleActionDisabled = !canPerformPinActions || isScheduling;

  async function handlePublishPin() {
    if (!canPerformPinActions) {
      alert("Select an account, board, and generated pin before publishing.");
      return;
    }

    if (!activeGeneratedPin.articleUrl) {
      alert("Article URL is missing from the selected pin.");
      return;
    }

    if (!activeGeneratedPin.imageUrl) {
      alert("Generated pin image is missing.");
      return;
    }

    if (!functionsUrl) {
      alert("Pinterest publish service is not configured.");
      return;
    }

    if (!selectedAccountHasBoardsWrite) {
      alert("Pinterest needs additional permissions (boards:write). Please reconnect once.");
      return;
    }

    setIsPublishing(true);
    try {
      const token = await getSupabaseAccessToken();
      if (!token) {
        throw new Error("Authentication expired. Please refresh.");
      }

      const res = await fetch(`${functionsUrl}/pinterest-create-pin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          pinterestAccountId: selectedPinterestAccount,
          boardId: selectedBoardId,
          imageUrl: activeGeneratedPin.imageUrl,
          title: activeGeneratedPin.sourceTitle || activeGeneratedPin.displayText || "Pinterest Pin",
          description:
            activeGeneratedPin.sourceDescription ||
            activeGeneratedPin.displayText ||
            "",
          tags: activeGeneratedPin.tags || [],
          link: activeGeneratedPin.articleUrl || "",
          articleUrl: activeGeneratedPin.articleUrl || ""
        }),
      });

      const payload = await res.json();
      if (!res.ok) {
        throw new Error(payload?.error?.message || payload?.error || "Publish request failed.");
      }

      alert("✅ Pin published successfully");
    } catch (error) {
      console.error("Publish pin failed", error);
      alert(`❌ Failed to publish pin: ${error?.message || error}`);
    } finally {
      setIsPublishing(false);
    }
  }

  async function handleScheduleSubmit() {
    if (!canPerformPinActions) {
      alert("Select an account, board, and generated pin before scheduling.");
      return;
    }

    if (!activeGeneratedPin.articleUrl) {
      alert("Article URL is missing from the selected pin.");
      return;
    }

    if (!activeGeneratedPin.imageUrl) {
      alert("Generated pin image is missing.");
      return;
    }

    if (!scheduleDateTime) {
      setScheduleError("Please choose a date and time.");
      return;
    }

    const scheduledDate = new Date(scheduleDateTime);
    if (Number.isNaN(scheduledDate.getTime())) {
      setScheduleError("Date/time is invalid.");
      return;
    }

    if (scheduledDate <= new Date()) {
      setScheduleError("Choose a future date and time.");
      return;
    }

    if (!functionsUrl) {
      setScheduleError("Pinterest services are not configured.");
      return;
    }

    if (!selectedAccountHasBoardsWrite) {
      setScheduleError("Pinterest connection needs boards:write. Please reconnect once.");
      return;
    }

    setScheduleError("");
    setIsScheduling(true);

    try {
      const token = await getSupabaseAccessToken();
      if (!token) {
        throw new Error("Authentication expired. Please refresh.");
      }

      const res = await fetch(`${functionsUrl}/pinterest-create-pin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          pinterestAccountId: selectedPinterestAccount,
          boardId: selectedBoardId,
          imageUrl: activeGeneratedPin.imageUrl,
          title: activeGeneratedPin.sourceTitle || activeGeneratedPin.displayText || "Pinterest Pin",
          description:
            activeGeneratedPin.sourceDescription ||
            activeGeneratedPin.displayText ||
            "",
          tags: activeGeneratedPin.tags || [],
          link: activeGeneratedPin.articleUrl || "",
          scheduledAt: scheduledDate.toISOString(),
          articleUrl: activeGeneratedPin.articleUrl || ""
        }),
      });

      const payload = await res.json();
      if (!res.ok) {
        throw new Error(payload?.error?.message || payload?.error || "Scheduling request failed.");
      }

      alert(`✅ Pin scheduled for ${scheduledDate.toLocaleString()}`);
      setShowScheduleForm(false);
      setScheduleDateTime("");
    } catch (error) {
      console.error("Scheduling failed", error);
      alert(`❌ Failed to schedule pin: ${error?.message || error}`);
    } finally {
      setIsScheduling(false);
    }
  }







  return (
    <SidebarProvider>
      <div className="min-h-screen bg-[#f7f7f4] overflow-x-hidden">
        <Sidebar />

        <main className="main-content overflow-x-hidden">
          <div className="w-full max-w-screen-2xl mx-auto px-6 py-8 space-y-6">

            {/* ===== HEADER CARD ===== */}
            <div className="bg-[#fffefa] border border-gray-200 rounded-lg p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex-shrink-0 rounded-full bg-indigo-100 flex items-center justify-center">
                  📌
                </div>
                <div>
                  <h1 className="text-base sm:text-xl font-bold text-gray-900">
                    Create Pin
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">
                    Select an article, review your Pin, then create it.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate("/pin/library")}
                  className="flex-1 sm:flex-none text-center px-3 py-2 rounded-md border border-indigo-600 text-indigo-600 text-xs sm:text-sm font-medium hover:bg-indigo-50 transition"
                >
                  Pin Library
                </button>
              </div>
            </div>

            {recoveryInput && <section className="bg-blue-50 border border-blue-100 rounded-xl p-4 space-y-3">
              <div className="flex flex-wrap justify-between gap-2"><h2 className="font-semibold">Recovery Center — prepare new Pins</h2><button className="text-sm underline" onClick={() => { setArticles([]); setSelectedArticleIds([]); navigate('/pin/create', { replace: true, state: null }); }}>Exit recovery handoff</button></div>
              <p className="text-sm">Review the selected articles, choose/confirm a template, then generate and review the preview. Existing Pins stay unchanged.</p>
              {recoveryError && <p role="alert" className="text-red-700 text-sm">{recoveryError}</p>}
              {(recoveryData?.articles || []).map(a => <div key={a.id} className="border-t border-blue-100 pt-2 text-sm"><strong>{a.title}</strong><p>{a.recovery_action}{a.secondary_action && ` + ${a.secondary_action}`}</p><p className="text-gray-600">{a.recovery_reason}</p>

              </div>)}
              <label className="flex gap-2 text-sm"><input type="checkbox" checked={recoveryConfirmed} onChange={e => setRecoveryConfirmed(e.target.checked)} />I reviewed the articles and selected template for these new Pins.</label>
            </section>}

            {/* ===== MAIN GRID ===== */}
            <div className="flex flex-col gap-6">

              {/* ===== CONTENT SECTION ===== */}
              <div className="space-y-6">

                <div className="bg-[#fffefa] border border-gray-200 rounded-lg p-6">
                  <h2 className="text-lg font-semibold mb-4">
                    1. Select articles
                  </h2>

                  <div className="space-y-6">

                    {/* ── Content Source Selector ── */}
                    <div>
                      <p className="text-sm font-medium mb-2">Content Source</p>
                      <div className="flex rounded-md border border-gray-200 overflow-hidden">
                        {[
                          { value: "wordpress", label: "WordPress" },
                          { value: "etsy", label: "Etsy" },
                        ].map(({ value, label }) => (
                          <button
                            key={value}
                            type="button"
                            disabled={Boolean(recoveryInput)}
                            onClick={() => handleContentSourceChange(value)}
                            className={`flex-1 py-2 text-sm font-medium transition-colors ${
                              contentSource === value
                                ? "bg-sky-100 text-sky-900"
                                : "bg-white text-gray-600 hover:bg-gray-50"
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* ── WordPress: existing site + article selector ── */}
                    {contentSource === "wordpress" && (
                    <div>
                      <p className="text-sm font-medium mb-2">Select content</p>
                      <label
                        htmlFor="website-select"
                        className="text-xs font-medium text-gray-500"
                      >
                        Select Website
                      </label>
                      <select
                        id="website-select"
                        disabled={Boolean(recoveryInput)}
                        value={selectedSite}
                        onChange={(e) => handleSiteSelection(e.target.value)}
                        className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                      >
                        <option value="">Select a website</option>
                        {sites?.map((site) => (
                          <option key={site?.id} value={site?.id}>
                            {site?.name}
                          </option>
                        ))}
                      </select>

                      {selectedSite && !recoveryInput && <div className="mt-3 flex flex-wrap gap-2">
                        <select aria-label="Year" value={articleYear} onChange={e => setArticleYear(e.target.value)} className="border rounded-md px-3 py-2 text-sm">
                          <option value="">All Years</option>
                          {availableYears.map(year => <option key={year} value={year}>{year}</option>)}
                        </select>
                        <select aria-label="Month" value={articleMonth} onChange={e => setArticleMonth(e.target.value)} className="border rounded-md px-3 py-2 text-sm">
                          <option value="">All Months</option>
                          {['January','February','March','April','May','June','July','August','September','October','November','December'].map((month, i) => <option key={month} value={String(i + 1).padStart(2, '0')}>{month}</option>)}
                        </select>
                        <input aria-label="Search articles" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search articles..." className="flex-1 min-w-[180px] border rounded-md px-3 py-2 text-sm" />
                      </div>}
                      {articleError && <p role="alert" className="text-sm text-red-600 mt-2">{articleError}</p>}

                      <div className="mt-3 border rounded-md p-4 text-sm text-gray-600 max-h-[480px] overflow-y-auto">
                        {!selectedSite ? (
                          <p className="text-center text-gray-400">
                            Select a site to view articles
                          </p>
                        ) : loadingArticles ? (
                          <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                            <svg
                              className="animate-spin h-6 w-6"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            <span className="mt-2 text-xs">Loading articles...</span>
                          </div>
                        ) : filteredArticles?.length === 0 ? (
                          <p className="text-center text-gray-400">
                            No articles found
                          </p>
                        ) : (
                          <>
                            {visibleArticles.length > 0 && (
                              <div className="flex items-center gap-2 pb-2 mb-1 border-b border-gray-100">
                                <input
                                  type="checkbox"
                                  id="select-all-articles"
                                  checked={visibleArticles.every((a) => selectedArticleIds.includes(a.id))}
                                  onChange={() => {
                                    const allSelected = visibleArticles.every((a) => selectedArticleIds.includes(a.id));
                                    if (allSelected) {
                                      const visibleIds = new Set(visibleArticles.map((a) => a.id));
                                      setSelectedArticleIds((prev) => prev.filter((id) => !visibleIds.has(id)));
                                    } else {
                                      const visibleIds = visibleArticles.map((a) => a.id);
                                      setSelectedArticleIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
                                    }
                                  }}
                                  className="cursor-pointer"
                                />
                                <label htmlFor="select-all-articles" className="text-xs font-medium text-gray-600 cursor-pointer select-none">
                                  Select all ({visibleArticles.length})
                                </label>
                                <label className="ml-auto flex items-center gap-2 text-xs font-medium text-gray-600 cursor-pointer">
                                  <input type="checkbox" checked={useFeaturedImage} onChange={e => setUseFeaturedImage(e.target.checked)} />
                                  Use Featured Image
                                </label>
                              </div>
                            )}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                              {visibleArticles.map((article) => <label key={article.id} className={`relative flex flex-col overflow-hidden rounded-lg border cursor-pointer transition-colors ${selectedArticleIds.includes(article.id) ? 'border-green-800 ring-1 ring-green-800 bg-green-50/30' : 'border-gray-200 bg-white hover:border-gray-400'}`}>
                                <input type="checkbox" aria-label={`Select ${article.title}`} checked={selectedArticleIds.includes(article.id)} disabled={isGenerating} onChange={() => toggleArticle(article.id)} className="absolute top-3 right-3 w-5 h-5 accent-green-800 z-10 cursor-pointer" />
                                {(article?.mainImage || article?.image || article?.featured_image_url || article?.featuredImage) ? <img src={article?.mainImage || article?.image || article?.featured_image_url || article?.featuredImage} alt="" loading="lazy" className="w-full h-36 object-cover" /> : <div className="w-full h-36 bg-gray-100" />}
                                <div className="p-3 space-y-2"><p className="font-medium text-sm line-clamp-2 pr-1">{article.title}</p><p className="text-xs text-gray-500">{new Date(article.date).toLocaleDateString()}</p></div>
                              </label>)}
                            </div>

                            {canShowMore && (
                              <button
                                type="button"
                                onClick={handleLoadMore}
                                disabled={isLoadingMore}
                                className="mt-3 w-full px-3 py-2 rounded-md border text-sm hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2"
                              >
                                {isLoadingMore ? (
                                  <>
                                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    Loading...
                                  </>
                                ) : (
                                  "Show more"
                                )}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    )}

                    {/* ── Etsy: account selector + sync + listings ── */}
                    {contentSource === "etsy" && (
                    <div>
                      <p className="text-sm font-medium mb-2">Select content</p>

                      <label
                        htmlFor="etsy-account-select"
                        className="text-xs font-medium text-gray-500"
                      >
                        Select Etsy Account
                      </label>
                      <div className="relative">
                        <select
                          id="etsy-account-select"
                          value={selectedEtsyAccount}
                          onChange={(e) => {
                            setSelectedEtsyAccount(e.target.value);
                            setArticles([]);
                            setSelectedArticleIds([]);
                            setEtsyListingsError(null);
                          }}
                          className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                        >
                          <option value="">Select an Etsy account</option>
                          {etsyAccounts?.map((account) => (
                            <option key={account?.id} value={account?.id}>
                              {account?.shop_name || account?.username || "Etsy Account"}
                            </option>
                          ))}
                        </select>
                        {loadingEtsyAccounts && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                            Loading...
                          </span>
                        )}
                      </div>
                      {!loadingEtsyAccounts && !etsyAccountsError && !etsyAccounts.length && (
                        <p className="text-xs text-gray-400 mt-1">
                          No Etsy accounts connected yet.
                        </p>
                      )}
                      {etsyAccountsError?.title && (
                        <p className="text-xs text-red-500 mt-1">{etsyAccountsError.title}</p>
                      )}

                      {selectedEtsyAccount && (
                        <button
                          type="button"
                          onClick={handleSyncEtsyListings}
                          disabled={isSyncingListings}
                          className="mt-3 w-full px-3 py-2 rounded-md border border-indigo-600 text-indigo-600 text-sm font-medium hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                        >
                          {isSyncingListings ? (
                            <>
                              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                              Syncing Listings...
                            </>
                          ) : (
                            "Sync Listings"
                          )}
                        </button>
                      )}

                      {articles.length > 0 && !isSyncingListings && (
                        <input
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Search listings..."
                          className="mt-3 w-full border rounded-md px-3 py-2 text-sm"
                        />
                      )}

                      <div className="mt-3 border rounded-md p-4 text-sm text-gray-600 max-h-[480px] overflow-y-auto">
                        {!selectedEtsyAccount ? (
                          <p className="text-center text-gray-400">
                            Select an Etsy account to sync listings
                          </p>
                        ) : isSyncingListings ? (
                          <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                            <svg
                              className="animate-spin h-6 w-6"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            <span className="mt-2 text-xs">Syncing listings...</span>
                          </div>
                        ) : etsyListingsError ? (
                          <p className="text-center text-amber-600 text-xs">{etsyListingsError}</p>
                        ) : articles.length === 0 ? (
                          <p className="text-center text-gray-400">
                            Click "Sync Listings" to load your active listings
                          </p>
                        ) : filteredArticles.length === 0 ? (
                          <p className="text-center text-gray-400">No listings match your search</p>
                        ) : (
                          <>
                            <div className="flex items-center gap-2 pb-2 mb-2 border-b border-gray-100">
                              <input
                                type="checkbox"
                                id="use-listing-main-image"
                                checked={useListingMainImage}
                                onChange={(e) => setUseListingMainImage(e.target.checked)}
                                className="cursor-pointer accent-indigo-600"
                              />
                              <label htmlFor="use-listing-main-image" className="text-xs font-medium text-gray-700 cursor-pointer select-none">
                                Use listing main image
                              </label>
                            </div>
                            {visibleArticles.length > 0 && (
                              <div className="flex items-center gap-2 pb-2 mb-1 border-b border-gray-100">
                                <input
                                  type="checkbox"
                                  id="select-all-listings"
                                  checked={visibleArticles.every((a) => selectedArticleIds.includes(a.id))}
                                  onChange={() => {
                                    const allSelected = visibleArticles.every((a) => selectedArticleIds.includes(a.id));
                                    if (allSelected) {
                                      const visibleIds = new Set(visibleArticles.map((a) => a.id));
                                      setSelectedArticleIds((prev) => prev.filter((id) => !visibleIds.has(id)));
                                    } else {
                                      const visibleIds = visibleArticles.map((a) => a.id);
                                      setSelectedArticleIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
                                    }
                                  }}
                                  className="cursor-pointer"
                                />
                                <label htmlFor="select-all-listings" className="text-xs font-medium text-gray-600 cursor-pointer select-none">
                                  Select all ({visibleArticles.length})
                                </label>
                              </div>
                            )}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                              {visibleArticles.map((listing) => <label key={listing.id} className={`relative flex flex-col overflow-hidden rounded-lg border cursor-pointer transition-colors ${selectedArticleIds.includes(listing.id) ? 'border-green-800 ring-1 ring-green-800 bg-green-50/30' : 'border-gray-200 bg-white hover:border-gray-400'}`}>
                                <input type="checkbox" aria-label={`Select ${listing.title}`} checked={selectedArticleIds.includes(listing.id)} disabled={isGenerating} onChange={() => toggleArticle(listing.id)} className="absolute top-3 right-3 w-5 h-5 accent-green-800 z-10 cursor-pointer" />
                                {(listing?.mainImage || listing?.image || listing?.featured_image_url || listing?.featuredImage) ? <img src={listing?.mainImage || listing?.image || listing?.featured_image_url || listing?.featuredImage} alt="" loading="lazy" className="w-full h-36 object-cover" /> : <div className="w-full h-36 bg-gray-100" />}
                                <div className="p-3 space-y-2"><p className="font-medium text-sm line-clamp-2 pr-1">{listing.title}</p><p className="text-xs text-gray-500">{new Date(listing.date).toLocaleDateString()}</p></div>
                              </label>)}
                            </div>
                            {etsyHasMore && (
                              <div className="pt-3 pb-1 text-center">
                                <button
                                  type="button"
                                  onClick={handleLoadMoreListings}
                                  disabled={isLoadingMoreListings}
                                  className="w-full px-3 py-2 rounded-md border border-indigo-600 text-indigo-600 text-sm font-medium hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                                >
                                  {isLoadingMoreListings ? (
                                    <>
                                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                      </svg>
                                      Loading...
                                    </>
                                  ) : (
                                    `Show More (${etsyOffset} of ${etsyTotal})`
                                  )}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    )}

                    <section className="border-t border-gray-200 pt-6 space-y-4">
                      <div><h2 className="text-lg font-semibold">2. Review your Pin</h2><p className="text-sm text-gray-500">Choose a template and review the image and text before creating.</p></div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                        <div className="min-w-0 space-y-4">
                <h2 className="text-sm font-medium mb-3">
                  Choose Template
                </h2>

                <div
                  className={`rounded-lg bg-slate-50/70 transition overflow-hidden ${
                    loadingArticles || (contentSource === "etsy" ? useListingMainImage : useFeaturedImage) ? "pointer-events-none opacity-60" : ""
                  }`}
                >
                  <div className="px-3 py-4">
                    <div
                      className="flex gap-4 overflow-x-scroll pb-3"
                      style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "thin" }}
                    >
                      {(contentSource === "etsy" ? etsyTemplateOptions : templateOptions).map((template) => {
                        const isSelected =
                          selectedTemplate === template.id ||
                          (template.id === "template1" && selectedTemplate === "template_1");
                        return (
                          <button
                            key={template.id}
                            type="button"
                            onClick={() => setSelectedTemplate(template.id)}
                            className={`flex-shrink-0 w-[110px] h-[155px] rounded-md p-0 border-0 bg-transparent transition duration-300 ${
                              isSelected
                                ? "ring-2 ring-green-800 ring-offset-2"
                                : "hover:scale-[1.02] hover:brightness-[1.04]"
                            }`}
                          >
                            <span className="sr-only">
                              {isSelected
                                ? `${template.alt} selected`
                                : `Select ${template.alt}`}
                            </span>
                            <img
                              src={template.src}
                              alt={template.alt}
                              className="h-full w-full object-contain rounded-md bg-white"
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {selectedTemplate && (
                  <p className="mt-3 text-sm text-gray-600">
                    Selected template:{" "}
                    <span className="font-medium text-indigo-600">
                      {selectedTemplate}
                    </span>
                  </p>
                )}


                          <label className="block text-sm font-medium" htmlFor="pin-draft-title">Pin title
                            <input id="pin-draft-title" value={reviewDraft.pinTitle} maxLength={100} disabled={!reviewArticle || generatingText || isGenerating} onChange={e => updateReviewDraft('pinTitle', e.target.value)} className="mt-2 w-full border border-gray-200 rounded-md p-3 bg-white disabled:bg-gray-50" />
                          </label>
                          <label className="block text-sm font-medium" htmlFor="pin-draft-description">Description
                            <textarea id="pin-draft-description" value={reviewDraft.pinDescription} maxLength={500} rows={5} disabled={!reviewArticle || generatingText || isGenerating} onChange={e => updateReviewDraft('pinDescription', e.target.value)} className="mt-2 w-full border border-gray-200 rounded-md p-3 bg-white disabled:bg-gray-50" />
                          </label>
                          <button type="button" disabled={!reviewArticle || generatingText || isGenerating || !activeProfile?.id} onClick={handleGenerateText} className="px-4 py-2.5 bg-[#e6edda] text-[#234c36] hover:bg-[#d9e5c8] rounded-md font-semibold text-sm disabled:opacity-50">{generatingText ? `Generating text ${textProgress.completed} / ${textProgress.total}` : 'Generate new text'}</button>
                          {textProgress.total > 0 && <div className="space-y-2" aria-live="polite">
                            <p className="text-sm text-gray-600">{textProgress.completed} / {textProgress.total} articles generated{textFailures.length > 0 ? ` · ${textFailures.length} failed` : ''}</p>
                            <progress aria-label="Article text generation" value={textProgress.completed} max={textProgress.total} className="w-full h-2 accent-green-800" />
                          </div>}
                          {textFailures.length > 0 && <ul role="alert" className="space-y-1 text-sm text-red-700">{textFailures.map(failure => <li key={failure.articleId}>{failure.title} (ID {failure.articleId}): {failure.message} Previous text kept.</li>)}</ul>}
                        </div>
                        <aside className="min-w-0 bg-[#f7f6f2] rounded-lg border border-gray-200 p-4 lg:sticky lg:top-4" aria-label="Live Pin preview">
                          <p className="text-xs font-medium text-gray-500 mb-3">PIN PREVIEW</p>
                          <div className="flex gap-3 overflow-x-auto pb-3 mb-4" aria-label="Selected Pin previews" style={{ scrollbarWidth: 'thin' }}>
                            {selectedArticles.map(article => {
                              const draft = draftFor(article);
                              const image = article.mainImage || article.image || article.featured_image_url || article.featuredImage;
                              const selected = reviewArticle?.id === article.id;
                              return <button key={article.id} type="button" aria-label={`Preview article ${article.id}: ${article.title}`} aria-pressed={selected} onClick={() => setReviewArticleId(article.id)} className={`w-36 flex-shrink-0 text-left rounded-md border overflow-hidden ${selected ? 'border-green-800 ring-1 ring-inset ring-green-800 bg-green-50' : 'border-gray-200 bg-white hover:border-gray-400'}`}>
                                {image ? <img src={image} alt="" className="w-full h-28 object-cover" /> : <div className="h-28 bg-gray-100" />}
                                <div className="p-2 space-y-1"><p className="text-xs font-semibold line-clamp-2">{draft.pinTitle}</p><p className="text-xs text-gray-600 line-clamp-2">{draft.pinDescription}</p></div>
                              </button>;
                            })}
                          </div>
                          <div className="max-w-sm mx-auto bg-white border border-gray-200 rounded-lg overflow-hidden">
                            {reviewImage ? <img src={reviewImage} alt={reviewArticle?.title || 'Selected article image'} className="w-full max-h-[400px] object-contain bg-[#f7f6f2]" /> : <div className="h-64 flex items-center justify-center text-sm text-gray-500">Select an article to preview</div>}
                            <div className="p-4 space-y-2 break-words"><h3 className="font-semibold text-gray-900">{reviewDraft.pinTitle || 'Your Pin title'}</h3><p className="text-sm text-gray-600 whitespace-pre-wrap">{reviewDraft.pinDescription}</p></div>
                          </div>
                          {reviewArticle && <p className="text-xs text-gray-500 mt-3 text-center">{(contentSource === 'wordpress' ? useFeaturedImage : useListingMainImage) ? 'Using the original image' : 'Article image · your chosen template is applied when you create the Pin'}</p>}
                        </aside>
                      </div>
                    </section>

                    <div className="space-y-4 border-t border-gray-100 pt-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700">
                          3. Create your Pin
                        </p>
                      </div>

                      <div className="space-y-2">
                        <label
                          htmlFor="pinterest-account"
                          className="block text-xs font-medium text-gray-500"
                        >
                          Select Pinterest Account
                        </label>
                        <div className="relative">
                          <select
                            id="pinterest-account"
                            value={selectedPinterestAccount}
                            onChange={(e) => setSelectedPinterestAccount(e.target.value)}
                            className="w-full border rounded-md px-3 py-2 text-sm"
                          >
                            <option value="">Select a Pinterest account</option>
                            {pinterestAccounts?.map((account) => (
                              <option key={account?.id} value={account?.id}>
                                {account?.username || "Pinterest Account"}
                              </option>
                            ))}
                          </select>
                          {accountsLoading && (
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                              Loading...
                            </span>
                          )}
                        </div>
                        {!accountsLoading && !accountsError && !pinterestAccounts.length && (
                          <p className="text-xs text-gray-400">
                            Connect your Pinterest account via the Accounts page.
                          </p>
                        )}
                        {accountsError?.title && (
                          <p className="text-xs text-red-500 mt-1">{accountsError.title}</p>
                        )}
                        {accountsError?.details && (
                          <p className="text-xs text-red-400">{accountsError.details}</p>
                        )}
                        {!accountsLoading && !accountsError?.title && !selectedAccountHasBoardsWrite && (
                          <p className="text-xs text-yellow-600 mt-1">
                            Pinterest needs additional permissions for publishing.
                            Please reconnect to grant them.
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label
                          htmlFor="pinterest-board"
                          className="block text-xs font-medium text-gray-500"
                        >
                          Select Board
                        </label>
                        <div className="relative">
                          <select
                            id="pinterest-board"
                            value={selectedBoardId}
                            onChange={(e) => setSelectedBoardId(e.target.value)}
                            disabled={!selectedPinterestAccount || boardsLoading}
                            className="w-full border rounded-md px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <option value="">
                              {boardsLoading ? "Loading boards..." : "Select a board"}
                            </option>
                            {boards?.map((board) => (
                              <option key={board?.id} value={board?.id}>
                                {board?.name || board?.title || board?.id}
                              </option>
                            ))}
                          </select>
                          {boardsLoading && (
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                              Loading...
                            </span>
                          )}
                        </div>
                        {boardsError?.title && (
                          <p className={`text-xs mt-1 ${boardsError.needsReconnect ? "text-amber-600" : "text-red-500"}`}>
                            {boardsError.title}
                            {boardsError.needsReconnect && (
                              <button
                                type="button"
                                onClick={() => navigate("/pin/accounts")}
                                className="ml-2 underline text-indigo-600"
                              >
                                Go to Accounts
                              </button>
                            )}
                          </p>
                        )}
                        {boardsError?.details && !boardsError.needsReconnect && (
                          <p className="text-xs text-red-400">{boardsError.details}</p>
                        )}
                        {selectedPinterestAccount && !boardsLoading && !boards.length && (
                          <p className="text-xs text-gray-400">
                            No boards found for this account yet.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <button
                    disabled={
                      isGenerating || generatingText || !activeProfile?.id ||
                      (recoveryInput && (!recoveryData || !recoveryConfirmed || !sites.some(site => site.id === recoveryData.siteId) || activeProfile?.id !== recoveryInput.profileId)) ||
                      loadingArticles ||
                      isSyncingListings ||
                      !selectedArticleIds.length ||
                      (contentSource === "wordpress" && !selectedSite) ||
                      (contentSource === "etsy" && !selectedEtsyAccount)
                    }
                    onClick={handleGeneratePins}
                    className="mt-6 w-full px-4 py-2 rounded-md bg-[#20543b] text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:bg-[#173f2c] transition-colors"
                  >
                    {isGenerating ? `Generating Pins ${generationProgress.completed} / ${generationProgress.total}` : "Create Pin"}
                  </button>
                  {generationProgress.total > 0 && <div className="mt-3" aria-live="polite">
                    <p className="text-xs text-gray-600 mb-1">{isGenerating ? 'Generating Pins' : 'Pins generated'} — {generationProgress.completed} / {generationProgress.total}</p>
                    <div role="progressbar" aria-label="Pins generated" aria-valuemin={0} aria-valuemax={generationProgress.total} aria-valuenow={generationProgress.completed} className="h-1.5 rounded-full bg-green-100 overflow-hidden">
                      <div className="h-full bg-green-500 transition-[width]" style={{ width: `${generationProgress.completed / generationProgress.total * 100}%` }} />
                    </div>
                  </div>}                {generatedPins.length > 0 && <div className="mt-6">
                  <p className="text-sm text-gray-500 text-center">
                    Generated pin preview
                  </p>
                  <div className="mt-3 rounded-2xl bg-white/80 border border-gray-100 shadow-sm overflow-hidden">
                    <div
                      className="flex gap-3 overflow-x-auto px-3 py-4"
                      style={{ WebkitOverflowScrolling: "touch" }}
                    >
                      {(() => {
                        const previewEntries = [...generatedPins];
                        return previewEntries.map((pin, idx) => {
                          if (!pin) {
                            return (
                              <div
                                key={`preview-placeholder-${idx}`}
                                className="flex-shrink-0 min-w-[140px] h-[200px] rounded-[16px] bg-gray-50 flex items-center justify-center text-gray-400 text-2xl font-semibold"
                              >
                                +
                              </div>
                            );
                          }

                          const isSelected = selectedGeneratedPinId === pin.id;
                          return (
                            <button
                              key={pin.id}
                              type="button"
                              onClick={() => setSelectedGeneratedPinId(pin.id)}
                              className={`flex-shrink-0 min-w-[140px] h-[200px] rounded-[16px] p-0 border-0 bg-transparent transition duration-250 ${
                                isSelected
                                  ? "scale-[1.04] shadow-[0_12px_24px_rgba(15,23,42,0.2)]"
                                  : "hover:scale-[1.02]"
                              }`}
                            >
                              <span className="sr-only">
                                {isSelected
                                  ? `${pin.displayText || "Generated pin"} selected`
                                  : `Select ${pin.displayText || "generated pin"}`}
                              </span>
                              <img
                                src={pin.imageUrl}
                                alt={pin.displayText || "Generated pin"}
                                className="h-full w-full object-contain rounded-[14px]"
                              />
                            </button>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>}

                </div>
              </div>
            </div>

          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
